import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Grid, 
  CircularProgress,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Stack,
  IconButton,
  InputAdornment
} from '@mui/material';
import { ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../components/SearchableSelect';
import carteraService, { Cartera } from '../../services/carteraService';
import ImputableSearch from '../../components/ImputableSearch';
import imputableService, { Imputable } from '../../services/imputableService';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`cartera-tabpanel-${index}`}
      aria-labelledby={`cartera-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface CarteraFormData {
  nroBanco: number;
  nombreBanco: string;
  tipoDeCuenta?: string;
  nroDeCuenta?: string;
  cbu?: string;
  titular?: string;
  sucursal?: string;
  nroCtaCble?: number | null;
  mcpPermitidos1?: number | null;
  mcpPermitidos2?: number | null;
  mcpPermitidos3?: number | null;
  mcpPermitidos4?: number | null;
  mcpPermitidos5?: number | null;
  mcpPermitidos6?: number | null;
  mcpPermitidos7?: number | null;
  mcpPermitidos8?: number | null;
  mcpPermitidos9?: number | null;
  mcpPermitidos10?: number | null;
}

const defaultForm: CarteraFormData = {
  nroBanco: 0,
  nombreBanco: '',
  tipoDeCuenta: '',
  nroDeCuenta: '',
  cbu: '',
  titular: '',
  sucursal: '',
  nroCtaCble: null,
  mcpPermitidos1: null,
  mcpPermitidos2: null,
  mcpPermitidos3: null,
  mcpPermitidos4: null,
  mcpPermitidos5: null,
  mcpPermitidos6: null,
  mcpPermitidos7: null,
  mcpPermitidos8: null,
  mcpPermitidos9: null,
  mcpPermitidos10: null,
};

const CarteraForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { nroBanco } = useParams();
  const [formData, setFormData] = useState<CarteraFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [imputableSearchOpen, setImputableSearchOpen] = useState(false);
  const [imputableDisplay, setImputableDisplay] = useState<{ nroCuenta: number; nomCuenta: string } | null>(null);

  const loadCartera = useCallback(async () => {
    if (!nroBanco) return;
    try {
      setLoading(true);
      const cartera = await carteraService.getCartera(Number(nroBanco));
      
      // Normalizar el tipo de cuenta para que coincida con los valores del Select
      let tipoDeCuentaNormalizado = '';
      const tipoDeCuentaRaw = (cartera.tipoDeCuenta || '').trim();
      if (tipoDeCuentaRaw) {
        // Mapear posibles variaciones a los valores exactos del Select
        const tipoLower = tipoDeCuentaRaw.toLowerCase();
        if (tipoLower.includes('caja') && tipoLower.includes('ahorro')) {
          tipoDeCuentaNormalizado = 'Caja de Ahorro';
        } else if (tipoLower.includes('cuenta') && tipoLower.includes('corriente')) {
          tipoDeCuentaNormalizado = 'Cuenta Corriente';
        } else if (tipoLower.includes('cartera')) {
          tipoDeCuentaNormalizado = 'Cartera';
        } else {
          // Si no coincide, usar el valor original (puede que sea uno de los valores correctos)
          tipoDeCuentaNormalizado = tipoDeCuentaRaw;
        }
      }
      
      setFormData({
        nroBanco: cartera.nroBanco,
        nombreBanco: cartera.nombreBanco || '',
        tipoDeCuenta: tipoDeCuentaNormalizado,
        nroDeCuenta: cartera.nroDeCuenta || '',
        cbu: cartera.cbu || '',
        titular: cartera.titular || '',
        sucursal: cartera.sucursal || '',
        nroCtaCble: cartera.nroCtaCble ?? null,
        mcpPermitidos1: cartera.mcpPermitidos1 ?? null,
        mcpPermitidos2: cartera.mcpPermitidos2 ?? null,
        mcpPermitidos3: cartera.mcpPermitidos3 ?? null,
        mcpPermitidos4: cartera.mcpPermitidos4 ?? null,
        mcpPermitidos5: cartera.mcpPermitidos5 ?? null,
        mcpPermitidos6: cartera.mcpPermitidos6 ?? null,
        mcpPermitidos7: cartera.mcpPermitidos7 ?? null,
        mcpPermitidos8: cartera.mcpPermitidos8 ?? null,
        mcpPermitidos9: cartera.mcpPermitidos9 ?? null,
        mcpPermitidos10: cartera.mcpPermitidos10 ?? null,
      });
      if (cartera.nroCtaCble != null && Number(cartera.nroCtaCble) > 0) {
        try {
          const imputable = await imputableService.getImputable(Number(cartera.nroCtaCble));
          if (imputable) {
            setImputableDisplay({ nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || imputable.descripcioncta || '').trim() });
          } else {
            setImputableDisplay(null);
          }
        } catch {
          setImputableDisplay(null);
        }
      } else {
        setImputableDisplay(null);
      }
    } catch (error) {
      enqueueSnackbar('Error al cargar la cartera', { variant: 'error' });
      navigate('/tesoreria/carteras');
    } finally {
      setLoading(false);
    }
  }, [nroBanco, enqueueSnackbar, navigate]);

  useEffect(() => {
    if (nroBanco) loadCartera();
  }, [nroBanco, loadCartera]);

  const valorDisplayCuentaContable = (): string => {
    if (imputableDisplay) return `${imputableDisplay.nroCuenta} - ${imputableDisplay.nomCuenta || ''}`;
    const num = formData.nroCtaCble;
    return num != null && num !== 0 ? String(num) : '';
  };

  const handleImputableSelect = (imputable: Imputable) => {
    setFormData(prev => ({ ...prev, nroCtaCble: imputable.nroCuenta }));
    setImputableDisplay({ nroCuenta: imputable.nroCuenta, nomCuenta: (imputable.nomCuenta || imputable.descripcioncta || '').trim() });
    setImputableSearchOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = new Set(['nroBanco', 'mcpPermitidos1', 'mcpPermitidos2', 'mcpPermitidos3', 'mcpPermitidos4', 'mcpPermitidos5', 'mcpPermitidos6', 'mcpPermitidos7', 'mcpPermitidos8', 'mcpPermitidos9', 'mcpPermitidos10']);
    if (numericFields.has(name)) {
      const parsed = value === '' ? (null as any) : Number(value);
      setFormData(prev => ({ ...prev, [name]: parsed } as any));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCheckboxChange = (field: keyof CarteraFormData, checked: boolean) => {
    setFormData(prev => ({ 
      ...prev, 
      [field]: checked ? 1 : 0 
    }));
  };

  // Función para verificar si un checkbox debe estar marcado
  // Considera cualquier valor numérico mayor a 0 como marcado
  const isChecked = (value: number | null | undefined): boolean => {
    return (value ?? 0) > 0;
  };

  // Mapeo de medios de cobro a campos mcpPermitidos
  const mediosDeCobro = [
    { label: 'Efectivo', field: 'mcpPermitidos1' as keyof CarteraFormData },
    { label: 'Cheques de Terceros', field: 'mcpPermitidos2' as keyof CarteraFormData },
    { label: 'Cheques Propios', field: 'mcpPermitidos3' as keyof CarteraFormData },
    { label: 'Tarjetas de Crédito', field: 'mcpPermitidos4' as keyof CarteraFormData },
    { label: 'Otros Medios', field: 'mcpPermitidos5' as keyof CarteraFormData },
    { label: 'Retenciones', field: 'mcpPermitidos6' as keyof CarteraFormData },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const requiredMissing: string[] = [];
      if (!formData.nroBanco || Number(formData.nroBanco) <= 0) requiredMissing.push('nroBanco');
      if (!String(formData.nombreBanco || '').trim()) requiredMissing.push('nombreBanco');
      if (requiredMissing.length) {
        enqueueSnackbar(`Complete los campos obligatorios: ${requiredMissing.join(', ')}`, { variant: 'warning' });
        setSaving(false);
        return;
      }

      const payload: Cartera = {
        ...formData,
        nroBanco: Number(formData.nroBanco),
        nombreBanco: String(formData.nombreBanco).trim(),
        tipoDeCuenta: formData.tipoDeCuenta?.trim() || '',
        nroDeCuenta: formData.nroDeCuenta?.trim() || '',
        cbu: formData.cbu?.trim() || '',
        titular: formData.titular?.trim() || '',
        sucursal: formData.sucursal?.trim() || '',
        nroCtaCble: formData.nroCtaCble ?? 0,
        mcpPermitidos1: formData.mcpPermitidos1 ?? 0,
        mcpPermitidos2: formData.mcpPermitidos2 ?? 0,
        mcpPermitidos3: formData.mcpPermitidos3 ?? 0,
        mcpPermitidos4: formData.mcpPermitidos4 ?? 0,
        mcpPermitidos5: formData.mcpPermitidos5 ?? 0,
        mcpPermitidos6: formData.mcpPermitidos6 ?? 0,
        mcpPermitidos7: formData.mcpPermitidos7 ?? 0,
        mcpPermitidos8: formData.mcpPermitidos8 ?? 0,
        mcpPermitidos9: formData.mcpPermitidos9 ?? 0,
        mcpPermitidos10: formData.mcpPermitidos10 ?? 0,
      };

      if (nroBanco) {
        await carteraService.updateCartera(Number(nroBanco), payload);
      } else {
        await carteraService.createCartera(payload);
      }

      enqueueSnackbar('Cartera guardada exitosamente', { variant: 'success' });
      navigate('/tesoreria/carteras');
    } catch (error: any) {
      let mostrado = false;
      const data = error?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        let detalle = '';
        for (const campo in data.errors) {
          if (Array.isArray(data.errors[campo])) {
            const nombreCampo = campo.charAt(0).toLowerCase() + campo.slice(1);
            detalle += `${nombreCampo}: ${data.errors[campo].join('; ')}\n`;
          }
        }
        if (detalle) {
          enqueueSnackbar(`Error en los datos: ${detalle}`, { variant: 'error', autoHideDuration: 12000 });
          mostrado = true;
        }
      }
      const messageText = (data?.message || data || error?.message || '').toString();
      if (!mostrado && messageText) {
        if (
          messageText.includes('PRIMARY KEY') ||
          messageText.toLowerCase().includes('duplicate') ||
          messageText.includes('Violation of PRIMARY KEY constraint')
        ) {
          enqueueSnackbar(`La cartera con código ${formData.nroBanco} ya existe.`, { variant: 'error' });
          mostrado = true;
        }
      }
      if (!mostrado) enqueueSnackbar('Error al guardar la cartera', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 3,
              fontWeight: 600,
              color: '#1976d2'
            }}
          >
            {nroBanco ? 'Editar Cartera' : 'Nueva Cartera'}
          </Typography>
          <form onSubmit={handleSubmit}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="pestañas de cartera"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '8px 8px 0 0',
                  }
                }}
              >
                <Tab label="General" />
                <Tab label="Medios de Cobro/Pago Disponibles" />
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="nroBanco" 
                    label="N° Cartera" 
                    type="number" 
                    value={formData.nroBanco} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!nroBanco}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField 
                    name="nombreBanco" 
                    label="Nombre Cartera" 
                    value={formData.nombreBanco} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    inputProps={{ maxLength: 30 }}
                    helperText={`${(formData.nombreBanco || '').length}/30`}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="sucursal" 
                    label="Sucursal" 
                    value={formData.sucursal || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ maxLength: 20 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SearchableSelect
                    label="Tipo de Cuenta"
                    value={formData.tipoDeCuenta || null}
                    onChange={(val) => setFormData((prev) => ({ ...prev, tipoDeCuenta: val ?? '' }))}
                    options={[
                      { value: 'Caja de Ahorro', label: 'Caja de Ahorro' },
                      { value: 'Cuenta Corriente', label: 'Cuenta Corriente' },
                      { value: 'Cartera', label: 'Cartera' },
                    ]}
                    allowEmpty
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="nroDeCuenta" 
                    label="N° de Cuenta" 
                    value={formData.nroDeCuenta || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="cbu" 
                    label="CBU" 
                    value={formData.cbu || ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="titular" 
                    label="Titular" 
                    value={formData.titular || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Cuenta Contable"
                    value={valorDisplayCuentaContable()}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#fafafa',
                      },
                    }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setImputableSearchOpen(true)}
                            edge="end"
                            color="primary"
                            title="Buscar cuenta contable"
                            sx={{ borderRadius: 2 }}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
                  Medios de Cobro/Pago Disponibles
                </Typography>
                <Grid container spacing={2}>
                  {mediosDeCobro.map((medio) => (
                    <Grid item xs={12} sm={6} md={4} key={medio.field}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked(formData[medio.field] as number | null | undefined)}
                            onChange={(e) => handleCheckboxChange(medio.field, e.target.checked)}
                            color="primary"
                          />
                        }
                        label={medio.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </TabPanel>

            <ImputableSearch
              open={imputableSearchOpen}
              onClose={() => setImputableSearchOpen(false)}
              onSelect={handleImputableSelect}
            />

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/tesoreria/carteras')} 
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CarteraForm;

